import ArticleLayout from './ArticleLayout';

export default function QCGuide() {
  return (
    <ArticleLayout
      title="Sugargoo QC Guide: How to Inspect Quality Check Photos Like a Pro"
      subtitle="Master the art of QC photo inspection with our detailed Sugargoo quality check guide. Learn what to look for in stitching, materials, logos, colors, and sizing to ensure every item you order meets your expectations before international shipping."
      category="QC Guide"
      breadcrumbs={[
        { name: 'Home', path: '/' },
        { name: 'QC Guide', path: '/sugargoo-qc-guide' },
      ]}
      schemaJson={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Sugargoo QC Guide: How to Inspect Quality Check Photos Like a Pro',
        description: 'Master the art of Sugargoo QC photo inspection with our detailed guide covering stitching, materials, logos, colors, and sizing.',
        author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
        datePublished: '2026-06-10',
        dateModified: '2026-06-18',
        publisher: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
      }}
    >
      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">What Are QC Photos and Why Do They Matter?</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          QC (Quality Check) photos are one of the most valuable features of the Sugargoo platform. After you place an order and the seller ships the item to the Sugargoo warehouse, the Sugargoo team photographs your actual product from multiple angles and uploads these images to your account. This gives you a chance to inspect the exact item you will receive before it is shipped internationally — a crucial step that can save you from receiving defective or incorrect products.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
          Understanding how to properly inspect QC photos is perhaps the single most important skill for any Sugargoo spreadsheet user. A thorough QC inspection can mean the difference between a satisfied purchase and a disappointing one. Here is our comprehensive guide to becoming a QC pro.
        </p>
      </section>

      <section className="mb-12">  
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">QC Inspection Checklist by Product Type</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Product</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Key QC Checks</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Red Flags</th>
              </tr>
            </thead>
            <tbody>
              {[
                { product: 'Sneakers', checks: 'Stitching, logo placement, shape, color, sole detail', flags: 'Glue stains, misaligned logos, wrong color shade' },
                { product: 'Hoodies', checks: 'Fabric weight, print quality, tag details, stitching', flags: 'Thin material, blurry prints, wrong tags' },
                { product: 'T-Shirts', checks: 'Print alignment, cotton quality, neck shape', flags: 'Crooked prints, rough fabric, stretched collar' },
                { product: 'Bags', checks: 'Hardware finish, stitching strength, logo accuracy', flags: 'Tarnished metal, loose threads, off-center logos' },
                { product: 'Watches', checks: 'Dial detail, bracelet quality, weight, movement', flags: 'Misaligned markers, light weight, rough edges' },
              ].map((row) => (
                <tr key={row.product} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground-900">{row.product}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.checks}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.flags}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Advanced QC Tips</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          When reviewing QC photos, always request additional photos if the initial set does not show a particular detail you want to verify. Sugargoo allows you to request close-ups of specific areas such as tags, logos, stitching, or any potential flaw you notice. Do not hesitate to use this feature — it is included in the service and can provide the clarity you need to make an informed decision.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          Another pro tip: compare the QC photos side-by-side with authentic retail images of the same product. Look for discrepancies in logo placement, color saturation, material texture, and overall proportions. While minor variations are normal, significant differences are a valid reason to request a return or exchange before approving international shipment.
        </p>
      </section>
    </ArticleLayout>
  );
}